import React, { useState } from 'react';
import { Upload, Download } from 'lucide-react';
import { toast } from 'sonner';
import { importCampaignRecipients } from '../lib/leadService';
import { formatApiError } from '../lib/campaignService';

function errorText(err) {
  if (err?.response?.status === 413) return 'File is too large for upload (max ~4 MB).';
  return formatApiError(err, 'Could not read the file');
}

/** Turn recipients into the "Name, number" lines the manual-numbers box already understands. */
export function recipientsToManualText(recipients) {
  return recipients
    .map((r) => {
      const name = String(r.name || '').replace(/[\r\n|,:]+/g, ' ').trim();
      return name ? `${name}, ${r.phone_number}` : r.phone_number;
    })
    .join('\n');
}

/**
 * Upload a CSV/Excel of numbers for one campaign. Nothing is sent from here: the cleaned
 * numbers are handed to onApply, which fills the manual-numbers box.
 */
export default function CampaignCsvUpload({ apiUrl, onApply }) {
  const [file, setFile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [saveNew, setSaveNew] = useState(true);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files?.[0] || null;
    e.target.value = '';
    setFile(f);
    setSummary(null);
    if (!f) return;
    setBusy(true);
    try {
      setSummary(await importCampaignRecipients(apiUrl, f, { saveNewToLeads: false }));
    } catch (err) {
      setFile(null);
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const handleUse = async () => {
    if (!file || !summary) return;
    setBusy(true);
    try {
      // The save is a second call so the user sees the counts before anything is written.
      const data = saveNew && summary.new_numbers > 0
        ? await importCampaignRecipients(apiUrl, file, { saveNewToLeads: true })
        : summary;
      onApply(data.recipients, data);
      if (saveNew && data.added_to_leads > 0) {
        toast.success(`${data.added_to_leads} new number(s) added to Leads`);
      }
      setFile(null);
      setSummary(null);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="campaign-csv-upload campaign-form-wide">
      <div className="campaign-import-actions">
        <label className="catalog-upload-btn">
          <Upload size={16} />
          {file ? file.name : 'Upload CSV / Excel of numbers'}
          <input
            type="file"
            accept=".csv,.xlsx,.xlsm,text/csv"
            style={{ display: 'none' }}
            onChange={handleFile}
            disabled={busy}
          />
        </label>
        <a className="campaign-secondary-btn" href="/samples/campaign_recipients_sample.csv" download>
          <Download size={16} /> Sample CSV
        </a>
      </div>

      {busy && !summary && <small>Checking file…</small>}

      {summary && (
        <div className="campaign-csv-summary">
          <p>
            <strong>{summary.valid_numbers}</strong> valid WhatsApp number{summary.valid_numbers === 1 ? '' : 's'} from{' '}
            {summary.total_rows} row{summary.total_rows === 1 ? '' : 's'}.
          </p>
          <ul>
            {summary.duplicates_in_file > 0 && <li>{summary.duplicates_in_file} repeated number(s) removed</li>}
            {summary.landlines_skipped > 0 && <li>{summary.landlines_skipped} landline(s) skipped (no WhatsApp)</li>}
            {summary.rejected_total > 0 && (
              <li>
                {summary.rejected_total} row(s) rejected
                {summary.rejected?.[0] ? ` (e.g. row ${summary.rejected[0].row}: ${summary.rejected[0].reason})` : ''}
              </li>
            )}
            <li>{summary.already_in_leads} already in Leads, {summary.new_numbers} new</li>
            {summary.phone_column_guessed && <li>Phone column was guessed from the data — check the numbers below.</li>}
          </ul>
          {summary.new_numbers > 0 && (
            <label className="campaign-csv-save">
              <input type="checkbox" checked={saveNew} onChange={(e) => setSaveNew(e.target.checked)} />
              Also add the {summary.new_numbers} new number{summary.new_numbers === 1 ? '' : 's'} to Leads
            </label>
          )}
          <button type="button" className="campaign-primary-btn" onClick={handleUse} disabled={busy}>
            {busy ? 'Working…' : `Use ${summary.valid_numbers} numbers`}
          </button>
        </div>
      )}
    </div>
  );
}
